import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Button from '../../components/Button';
import BalanceBadge from '../../components/BalanceBadge';
import { ArrowLeft, Trash2 } from 'lucide-react';
import journalEntriesAPI from '../../api/journalEntriesAPI';
import accountingClientsAPI from '../../api/accountingClientsAPI';
import ChartOfAccountsSearch from '../../components/ChartOfAccountsSearch';
import MoneyInput from '../../components/MoneyInput';
import VendorSearch from '../../components/VendorSearch';
import PredictiveMemoInput from '../../components/PredictiveMemoInput';
import { useAuth } from '../../context/AuthContext';
import useCompanyContext from '../../hooks/useCompanyContext';
import useGuardedMutation from '../../hooks/useGuardedMutation';
import { useVendorTerms } from '../../utils/terminology';

const NewJournalEntryPage = () => {
  const term = useVendorTerms();
  const navigate = useNavigate();
  const { selectedClient, isAuthReady } = useAuth();
  const { accountingClientId: contextAccountingClientId } = useCompanyContext();

  const [redirecting, setRedirecting] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  // Keep error as string (can be warning too)
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [entryNumber, setEntryNumber] = useState('');

  // ✅ Accounting client state for multi-tenant context
  const [accountingClients, setAccountingClients] = useState([]);
  const [selectedAccountingClient, setSelectedAccountingClient] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);

  // Per-line search meta (query + results count)
  const [lineSearchMeta, setLineSearchMeta] = useState({}); // { [index]: { query, count } }

  const [formData, setFormData] = useState({
    entryDate: new Date().toISOString().split('T')[0],
    description: '',
    memo: '',
    referenceNumber: '',
    source: 'manual',
    vendor: null, // Optional vendor selection
    lines: [
      {
        account: null, // COA _id
        accountName: '', // "1000 - Cash" display
        description: '',
        debit: 0,
        credit: 0,
        reference: '',
      },
    ],
  });

  useEffect(() => {
    if (!isAuthReady) return;
    loadAccountingClients();
  }, [isAuthReady]);

  useEffect(() => {
    if (contextAccountingClientId && contextAccountingClientId !== selectedAccountingClient) {
      setSelectedAccountingClient(contextAccountingClientId);
    }
  }, [contextAccountingClientId, selectedAccountingClient]);

  // ✅ Load accounting clients from API
  const loadAccountingClients = async () => {
    try {
      setContextLoading(true);
      const response = await accountingClientsAPI.getActive();
      const clients = response.data || response || [];
      setAccountingClients(clients);
      
      // Get selected client from context → localStorage → auto-select first
      const storedClientId = contextAccountingClientId || selectedClient?._id || localStorage.getItem('accountingClientId');
      if (storedClientId && clients.some(c => c._id === storedClientId)) {
        setSelectedAccountingClient(storedClientId);
      } else if (clients.length > 0) {
        setSelectedAccountingClient(clients[0]._id);
        localStorage.setItem('accountingClientId', clients[0]._id);
      }
      
      fetchInitialData();
    } catch (err) {
      console.error('Failed to load accounting clients:', err);
      setError('Failed to load accounting clients');
      fetchInitialData();
    } finally {
      setContextLoading(false);
    }
  };

  // ✅ Handle accounting client selection from header dropdown
  const handleAccountingClientSelect = (clientId) => {
    setSelectedAccountingClient(clientId);
    localStorage.setItem('accountingClientId', clientId);
    fetchInitialData();
  };
  const fetchInitialData = async () => {
    setInitialLoading(true);
    try {
      const entryNumberRes = await journalEntriesAPI.generateEntryNumber();
      setEntryNumber(entryNumberRes.data?.entryNumber || '');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load initial data');
    } finally {
      setInitialLoading(false);
    }
  };

  const addLine = () => {
    setFormData((prev) => ({
      ...prev,
      lines: [
        ...prev.lines,
        {
          account: null,
          accountName: '',
          description: '',
          debit: 0,
          credit: 0,
          reference: '',
        },
      ],
    }));
  };

  const removeLine = (index) => {
    setFormData((prev) => {
      if (prev.lines.length <= 1) return prev;
      const nextLines = prev.lines.filter((_, i) => i !== index);

      // Also clean meta for removed line + reindex remaining meta
      setLineSearchMeta((old) => {
        const next = {};
        nextLines.forEach((_, newIdx) => {
          const oldIdx = newIdx >= index ? newIdx + 1 : newIdx;
          if (old[oldIdx]) next[newIdx] = old[oldIdx];
        });
        return next;
      });

      return { ...prev, lines: nextLines };
    });
  };

  const updateLine = (index, field, value) => {
    setFormData((prev) => {
      const newLines = [...prev.lines];

      if (field === 'debit' || field === 'credit') {
        const num = parseFloat(value) || 0;

        // Ensure only one side is non-zero
        if (field === 'debit' && num > 0) newLines[index].credit = 0;
        if (field === 'credit' && num > 0) newLines[index].debit = 0;

        newLines[index][field] = num;
      } else {
        newLines[index][field] = value;
      }

      return { ...prev, lines: newLines };
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const isBlankLine = (line) => {
    const hasAccount = Boolean(line.account);
    const hasAmount = (line.debit || 0) > 0 || (line.credit || 0) > 0;
    return !hasAccount && !hasAmount;
  };

  const getSubstantiveLines = () => formData.lines.filter((line) => !isBlankLine(line));

  const calculateTotals = () => {
    const substantive = getSubstantiveLines();
    const totalDebit = substantive.reduce((sum, line) => sum + parseFloat(line.debit || 0), 0);
    const totalCredit = substantive.reduce((sum, line) => sum + parseFloat(line.credit || 0), 0);
    const difference = Math.abs(totalDebit - totalCredit);
    // Treat 0-1 substantive lines as not yet balanced.
    const isBalanced = substantive.length >= 2 && difference < 0.01;
    return { totalDebit, totalCredit, difference, isBalanced, substantiveCount: substantive.length };
  };

  const getFiscalYearAndPeriod = (dateString) => {
    const date = new Date(dateString);
    const fiscalYear = date.getFullYear();
    const accountingPeriod = new Date(date.getFullYear(), date.getMonth(), 1);
    return { fiscalYear, accountingPeriod };
  };

  // Strict validation (posting only)
  const validateForPost = () => {
    if (!formData.memo.trim()) return 'Memo is required';
    if (!formData.entryDate) return 'Entry date is required';

    const substantive = getSubstantiveLines();
    if (substantive.length < 2) {
      return 'At least 2 entry lines are required (one debit, one credit)';
    }

    for (const line of substantive) {
      const lineNumber = formData.lines.indexOf(line) + 1;
      if (!line.account) return `Line ${lineNumber}: Account is required`;
      if ((line.debit || 0) === 0 && (line.credit || 0) === 0)
        return `Line ${lineNumber}: Either debit or credit must be greater than 0`;
      if ((line.debit || 0) > 0 && (line.credit || 0) > 0)
        return `Line ${lineNumber}: Cannot have both debit and credit amounts`;
    }

    const { isBalanced } = calculateTotals();
    if (!isBalanced) return 'Journal entry must balance (debits must equal credits)';

    return null;
  };

  // Relaxed draft warnings (do not block save)
  const validateForDraftWarning = () => {
    const warnings = [];
    const { isBalanced } = calculateTotals();

    if (!isBalanced) warnings.push('Draft is out of balance');
    if (!formData.memo.trim()) warnings.push('Memo is empty');
    if (!formData.entryDate) warnings.push('Entry date is empty');

    formData.lines.forEach((line, i) => {
      const hasAnyAmount =
        (parseFloat(line.debit || 0) || 0) > 0 || (parseFloat(line.credit || 0) || 0) > 0;
      const hasAccount = !!line.account;
      if (hasAnyAmount && !hasAccount) warnings.push(`Line ${i + 1}: amount entered but no account selected`);
    });

    return warnings.length ? `Saved as draft with warnings: ${warnings.join(', ')}` : '';
  };

  // ✅ EXACTLY like VATInputForm: per-line select updates only that line
  const handleChartOfAccountSelect = useCallback((account, index) => {
    setFormData((prev) => {
      const newLines = [...prev.lines];

      // Clear selection
      if (!account) {
        newLines[index] = {
          ...newLines[index],
          account: null,
          accountName: '',
        };
        return { ...prev, lines: newLines };
      }

      newLines[index] = {
        ...newLines[index],
        account: account._id,
        accountName: `${account.accountNumber} - ${account.accountName}`,
      };

      return { ...prev, lines: newLines };
    });

    // Once selected, clear meta so "Add ..." doesn't show
    setLineSearchMeta((prev) => ({
      ...prev,
      [index]: { query: '', count: 1 },
    }));
  }, []);

  // ✅ per-line results tracking (so JE can show "Add ..." only when 0 results)
  const handleLineSearchResults = useCallback((index, meta) => {
    setLineSearchMeta((prev) => ({
      ...prev,
      [index]: meta || { query: '', count: 0 },
    }));
  }, []);

  // Parse "1000 - Cash" for prefill
  const parseAccountPrefill = (raw) => {
    const q = String(raw || '').trim();
    if (!q) return { accountNumber: '', accountName: '' };

    // If user types "1000 - Cash"
    const parts = q.split('-').map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      return { accountNumber: parts[0], accountName: parts.slice(1).join(' - ') };
    }

    // If "1000 Cash" or "1000"
    const match = q.match(/^(\d+)\s+(.*)$/);
    if (match) return { accountNumber: match[1], accountName: match[2] };

    return { accountNumber: '', accountName: q };
  };

  const handleAddAccountFromLine = (index) => {
    const meta = lineSearchMeta[index];
    const q = meta?.query || formData.lines[index]?.accountName || '';
    const prefill = parseAccountPrefill(q);

    // ✅ Navigate to your COA create screen with prefill (adjust route if needed)
    navigate('/chart-of-accounts/new', {
      state: {
        from: 'new-journal-entry',
        prefill,
      },
    });
  };

  const fiscalYearNum = useMemo(() => {
    const key = selectedAccountingClient ? `activeFiscalYear:${selectedAccountingClient}` : null;
    const headerFY = key ? parseInt(localStorage.getItem(key), 10) : null;
    return headerFY && !isNaN(headerFY) ? headerFY : null;
  }, [selectedAccountingClient]);

  // Compute selected account IDs and names for memo context
  const memoContext = useMemo(() => {
    const accountIds = formData.lines
      .map(l => l.account)
      .filter(Boolean);
    const uniqueAccountIds = [...new Set(accountIds)];
    
    const accountNames = formData.lines
      .map(l => l.accountName)
      .filter(Boolean)
      .slice(0, 2);
    
    return {
      entryDate: formData.entryDate,
      source: formData.source,
      vendorId: formData.vendor?._id,
      vendorName: formData.vendor?.name,
      accounts: uniqueAccountIds,
      accountNames,
      entityType: 'journal'
    };
  }, [formData.lines, formData.entryDate, formData.source, formData.vendor]);

  const { mutate: handleSubmit, isPending: loading } = useGuardedMutation(async (action) => {
    // Block only posting
    if (action === 'saveAndPost') {
      const strictError = validateForPost();
      if (strictError) {
        setError(strictError);
        return;
      }
      setError('');
    }

    // Draft: warn but don't block
    if (action === 'saveAsDraft') {
      const warning = validateForDraftWarning();
      setError(warning || '');
    }

    try {
      setSuccess('');

      const payload = {
        ...formData,
        ...(action === 'saveAsDraft' ? { status: 'draft' } : {}),
        accountingClient: selectedAccountingClient || null,
        fiscalYear: fiscalYearNum || null,
        vendor: formData.vendor?._id || null, // Include vendor ID if selected
        lines: formData.lines
          .filter((line) => !isBlankLine(line))
          .map((line) => ({
            account: line.account,
            description: line.description,
            reference: line.reference,
            debit: parseFloat(line.debit || 0),
            credit: parseFloat(line.credit || 0),
          })),
      };

      if (action === 'saveAsDraft') {
        const response = await journalEntriesAPI.create(payload);
        const createdId = response.data?._id;

        setSuccess('Journal entry saved as draft successfully');
        setRedirecting(true);
        navigate(`/journal-entry/${createdId}`);
        return;
      }

      if (action === 'saveAndPost') {
        const response = await journalEntriesAPI.create(payload);
        const created = response.data;
        const createdId = created?._id;

        if (!createdId) throw new Error('Create journal entry succeeded but returned no _id');

        const { fiscalYear, accountingPeriod } = getFiscalYearAndPeriod(created.entryDate);
        const createdLines = Array.isArray(created.lines) ? created.lines : [];

        const generalLedgerEntries = createdLines.map((line) => ({
          journalEntry: createdId,
          journalLine: line._id,
          account: line.account?._id || line.account,
          transactionDate: created.entryDate,
          fiscalYear,
          accountingPeriod,
          debit: line.debit,
          credit: line.credit,
          description: line.description || created.description,
          referenceNumber: line.reference || created.referenceNumber,
          source: created.source || 'manual',
        }));

        await journalEntriesAPI.postToGeneralLedger(createdId, generalLedgerEntries);

        setSuccess('Journal entry saved and posted successfully');
        setRedirecting(true);
        navigate('/journal-entry');
      }
    } catch (err) {
      setRedirecting(false);
      const backendMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to save journal entry';
      setError(backendMsg);
      throw err; // let useGuardedMutation know this attempt failed — keeps the idempotency key for retry
    }
  });

  const disabledUI = loading || redirecting || initialLoading;

  const { totalDebit, totalCredit, difference, isBalanced, substantiveCount } = calculateTotals();

  return (
    <Layout
      contextKind="accounting_client"
      contextOptions={accountingClients}
      activeContextId={selectedAccountingClient}
      onContextSelect={handleAccountingClientSelect}
    >
      <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-medium text-ink">
            <a href="/journal-entry" className="text-primary-500 hover:text-primary-600 hover:underline">Journal Entries</a> / New
          </h1>
        </div>

        {!!error && (
          <div className="rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-800 mb-4">
            {error}
          </div>
        )}

        {/* show out-of-balance message persistently */}
        {!isBalanced && (
          <div className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800 mb-4">
            {substantiveCount === 0
              ? 'No entry lines added yet. Add at least one debit and one credit line.'
              : `Journal entry is out of balance by ₱${difference.toFixed(2)}`}
          </div>
        )}

        {!!success && (
          <div className="rounded-lg border border-success-200 bg-success-50 px-4 py-3 text-sm text-success-700 mb-4">
            {success}
          </div>
        )}

        <div className="pb-28">
        {/* ✅ JE HEADER (restored) */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-medium text-ink mb-4">Entry Header</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Entry Number</label>
              <input
                type="text"
                value={entryNumber}
                className="w-full border rounded px-3 py-2 text-gray-600 bg-gray-100"
                disabled
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Entry Date *</label>
              <input
                type="date"
                lang="en-US"
                name="entryDate"
                value={formData.entryDate}
                onChange={(e) => setFormData({ ...formData, entryDate: e.target.value })}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
                disabled={disabledUI}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Reference Number</label>
              <input
                type="text"
                name="referenceNumber"
                value={formData.referenceNumber}
                onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Optional reference"
                disabled={disabledUI}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Source</label>
              <select
                name="source"
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                disabled={disabledUI}
              >
                <option value="manual">Manual</option>
                <option value="sales">Sales</option>
                <option value="purchase">Purchase</option>
                <option value="payment">Payment</option>
                <option value="receipt">Receipt</option>
                <option value="adjustment">Adjustment</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">{term.one} (optional)</label>
            <VendorSearch
              value={formData.vendor}
              onSelect={(vendor) => setFormData({ ...formData, vendor })}
              placeholder={`Search ${term.lower} by name, code, or TIN...`}
              disabled={disabledUI}
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Memo *</label>
            <PredictiveMemoInput
              value={formData.memo}
              onChange={(memo) => setFormData({ ...formData, memo })}
              disabled={disabledUI}
              required
              placeholder="Enter journal entry memo"
              accountingClientId={selectedAccountingClient}
              context={memoContext}
            />
          </div>


          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Description (optional)</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
              rows={2}
              placeholder="Optional additional notes or description"
              disabled={disabledUI}
            />
          </div>

        </div>

        {/* ENTRY LINES */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-medium text-ink">Entry Lines</h2>
            <Button
              type="button"
              onClick={addLine}
              variant="secondary"
              size="sm"
              disabled={disabledUI}
            >
              + Add Line
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account *
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Debit
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Credit
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {formData.lines.map((line, index) => {
                  const meta = lineSearchMeta[index] || { query: '', count: 0 };
                  const typedQuery = (meta.query || '').trim();
                  const noResults = typedQuery.length > 0 && meta.count === 0;
                  const hasSelectedAccount = !!line.account;

                  // ✅ Add padding to make dropdown more visible while searching
                  const needsDropdownSpace = typedQuery.length > 0 && !hasSelectedAccount;

                  return (
                    <tr key={index} className="hover:bg-gray-50 align-top">
                      <td className={`px-4 py-3 ${needsDropdownSpace ? 'pb-32' : ''}`}>
                        <div className="space-y-2">
                          <ChartOfAccountsSearch
                            value={line.accountName || ''}
                            onChange={(value) => updateLine(index, 'accountName', value)}
                            onSelect={(account) => handleChartOfAccountSelect(account, index)}
                            onSearchResults={(m) => handleLineSearchResults(index, m)}
                            placeholder="Search chart of accounts..."
                            showLinkedBadge={false}
                            disabled={disabledUI}
                          />

                          {/* ✅ Show Add suggestion ONLY if account does not exist yet */}
                          {!hasSelectedAccount && noResults && (
                            <button
                              type="button"
                              onMouseDown={(e) => e.preventDefault()} // prevents blur closing dropdown too early
                              onClick={() => handleAddAccountFromLine(index)}
                              className="text-sm text-primary-700 hover:text-primary-900 font-medium text-left"
                              disabled={disabledUI}
                            >
                              + Add “{typedQuery}” to Chart of Accounts
                            </button>
                          )}

                          {/* Optional helper text */}
                          {!hasSelectedAccount && noResults && (
                            <div className="text-xs text-gray-500">
                              No match found. Click above to create it in Chart of Accounts.
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={line.description}
                          onChange={(e) => updateLine(index, 'description', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                          placeholder="Line description"
                          disabled={disabledUI}
                        />
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <MoneyInput
                          value={line.debit || 0}
                          disabled={disabledUI}
                          onValueChange={(num) => updateLine(index, 'debit', num)}
                        />
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <MoneyInput
                          value={line.credit || 0}
                          disabled={disabledUI}
                          onValueChange={(num) => updateLine(index, 'credit', num)}
                        />
                      </td>


                      <td className="px-4 py-3 whitespace-nowrap">
                        <input
                          type="text"
                          value={line.reference}
                          onChange={(e) => updateLine(index, 'reference', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                          placeholder="Reference"
                          disabled={disabledUI}
                        />
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <Button
                          type="button"
                          variant="dangerGhost"
                          size="sm"
                          icon={Trash2}
                          onClick={() => removeLine(index)}
                          disabled={formData.lines.length <= 1 || disabledUI}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>
        </div>

        {/* Sticky balance + actions footer */}
        <div className="sticky bottom-0 z-10 -mx-6 mt-6 bg-white/95 backdrop-blur border-t border-ink-line">
          <div className="px-6 py-3 flex items-center justify-between gap-4 border-b border-ink-line/60">
            <BalanceBadge
              balanced={isBalanced}
              label={
                isBalanced
                  ? 'Balanced'
                  : substantiveCount === 0
                  ? 'No entries yet'
                  : `Out of balance by ${formatCurrency(difference)}`
              }
            />
            <div className="flex items-center gap-8">
              <div className="text-right">
                <div className="text-xs text-ink-body">Total debit</div>
                <div className="text-sm font-medium text-ink tabular-nums">{formatCurrency(totalDebit)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-ink-body">Total credit</div>
                <div className="text-sm font-medium text-ink tabular-nums">{formatCurrency(totalCredit)}</div>
              </div>
            </div>
          </div>

          <div className="px-6 py-3 flex items-center justify-between gap-4">
            <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/journal-entry')} disabled={disabledUI}>
              Back to list
            </Button>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/journal-entry')}
                disabled={disabledUI}
              >
                Cancel
              </Button>

              {/* Draft can save even with warnings */}
              <Button
                type="button"
                variant="outline"
                onClick={() => handleSubmit('saveAsDraft')}
                loading={loading}
                disabled={disabledUI}
              >
                {loading ? 'Saving...' : 'Save draft'}
              </Button>

              {/* Posting requires balanced */}
              <Button
                type="button"
                variant="primary"
                onClick={() => handleSubmit('saveAndPost')}
                loading={loading}
                disabled={disabledUI || !isBalanced}
              >
                {loading ? 'Posting...' : 'Post entry'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {redirecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm text-center">
            <div className="text-lg font-medium text-ink mb-2">Redirecting…</div>
            <div className="text-sm text-gray-600 mb-4">Opening the journal entry edit screen</div>
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
          </div>
        </div>
      )}
    </Layout>
  );
};

export default NewJournalEntryPage;
